import { AnimatePresence, motion } from "framer-motion";
import {
  BarChart3,
  CalendarDays,
  Check,
  Clock3,
  Fingerprint,
  Users,
  Wallet,
  type LucideIcon,
} from "lucide-react";
import { useState } from "react";
import appLeave from "../../../assets/images/home/product/app-leave.webp";
import appWorktime from "../../../assets/images/home/product/app-worktime.webp";
import webAttendance from "../../../assets/images/home/product/web-attendance.webp";
import webBranch from "../../../assets/images/home/product/web-branch.webp";
import webDirectory from "../../../assets/images/home/product/web-directory.webp";
import webGeneratePayroll from "../../../assets/images/home/product/web-generate-payroll.webp";
import webKpiSetup from "../../../assets/images/home/product/web-kpi-setup.webp";
import webLeave from "../../../assets/images/home/product/web-leave.webp";
import webPayroll from "../../../assets/images/home/product/web-payroll.webp";
import webPerformance from "../../../assets/images/home/product/web-performance.webp";
import webRoster from "../../../assets/images/home/product/web-roster.webp";
import webShiftAssign from "../../../assets/images/home/product/web-shift-assign.webp";
import cn from "../../../lib/cn";
import { PhoneFrame, ScreenFrame } from "./ProductFrames";
import SectionHeading from "./SectionHeading";

type Module = {
  key: string;
  label: string;
  icon: LucideIcon;
  title: string;
  description: string;
  points: string[];
  screen: { src: string; alt: string };
  /** Second screen layered over the main one — the app or a dialog. */
  companion?:
    | { kind: "phone"; src: string; alt: string }
    | { kind: "dialog"; src: string; alt: string; height: number };
};

const MODULES: Module[] = [
  {
    key: "attendance",
    label: "Attendance",
    icon: Fingerprint,
    title: "Attendance, wherever work happens",
    description:
      "Employees check in from the app with a selfie inside your geo-fence. You see who is in, who is late and every correction waiting for approval.",
    points: [
      "Selfie and geo-fenced check-in",
      "Live work time, breaks and overtime",
      "Correction requests with one-tap approval",
    ],
    screen: {
      src: webAttendance,
      alt: "HRx attendance list and pending corrections",
    },
    companion: {
      src: appWorktime,
      alt: "HRx app showing today's work time",
      kind: "phone",
    },
  },
  {
    key: "leave",
    label: "Leave",
    icon: CalendarDays,
    title: "Leave without the back-and-forth",
    description:
      "Your leave policy, applied the same way every time. Employees see their balance before they ask, and managers approve from one list.",
    points: [
      "Balances by leave type, always up to date",
      "Request and approval flow with status",
      "Organisation calendar with holidays",
    ],
    screen: { src: webLeave, alt: "HRx leave request and approval list" },
    companion: {
      src: appLeave,
      alt: "HRx app showing leave balances",
      kind: "phone",
    },
  },
  {
    key: "shifts",
    label: "Shifts & roster",
    icon: Clock3,
    title: "Rosters that fit your operation",
    description:
      "Plan day and night shifts for every team, mark weekly holidays and leave, and see the whole week at a glance.",
    points: [
      "Day, night and custom shifts",
      "Weekly roster per employee",
      "Grace periods and holidays built in",
    ],
    screen: { src: webRoster, alt: "HRx weekly roster calendar" },
    companion: {
      src: webShiftAssign,
      alt: "HRx dialog for adding a shift and assigning employees",
      kind: "dialog",
      height: 874,
    },
  },
  {
    key: "payroll",
    label: "Payroll",
    icon: Wallet,
    title: "Payroll in a few clicks",
    description:
      "Set your payroll cycle, salary structure and grades once. Additions, deductions and leave encashment flow into every salary sheet.",
    points: [
      "Monthly or daily payroll cycles",
      "Salary structure and grade management",
      "Pay sheets, bonuses and encashment",
    ],
    screen: {
      src: webPayroll,
      alt: "HRx payroll calendar, salary structure and grades",
    },
    companion: {
      src: webGeneratePayroll,
      alt: "HRx dialog for generating payroll by department",
      kind: "dialog",
      height: 770,
    },
  },
  {
    key: "performance",
    label: "Performance",
    icon: BarChart3,
    title: "Performance you can measure",
    description:
      "Set KPIs for each cycle, run appraisals and see how ratings are spread across the organisation.",
    points: [
      "KPI set-up per cycle",
      "Appraisal reports",
      "Performance trend across teams",
    ],
    screen: { src: webPerformance, alt: "HRx performance trend chart" },
    companion: {
      src: webKpiSetup,
      alt: "HRx dialog for setting up a KPI plan",
      kind: "dialog",
      height: 855,
    },
  },
  {
    key: "people",
    label: "People & branches",
    icon: Users,
    title: "Every employee and branch in one place",
    description:
      "A searchable directory of your people, and branches with their own working days, office hours and geo-fence.",
    points: [
      "Employee directory with filters",
      "Branches with working days and time zone",
      "Geo-fence radius per office",
    ],
    screen: { src: webDirectory, alt: "HRx employee directory" },
    companion: {
      src: webBranch,
      alt: "HRx add branch dialog with geo-fencing",
      kind: "dialog",
      height: 1112,
    },
  },
];

const ease = [0.16, 1, 0.3, 1] as const;

const Features = () => {
  const [activeKey, setActiveKey] = useState(MODULES[0].key);
  const active = MODULES.find((m) => m.key === activeKey) ?? MODULES[0];

  return (
    <section className="overflow-hidden bg-white px-4 py-24 sm:px-6 sm:py-28 lg:px-8">
      <div className="mx-auto max-w-7xl">
        <SectionHeading
          eyebrow="Platform"
          title="Everything HR, in one place"
          description="From the first check-in to the monthly payslip — one platform for the whole employee lifecycle, with an admin panel that keeps you in control."
        />

        {/* Module tabs */}
        <div className="mt-12 flex justify-center">
          <div
            role="tablist"
            aria-label="HRx modules"
            className="-mx-4 flex max-w-[calc(100%+2rem)] gap-1.5 overflow-x-auto px-4 pb-1 sm:mx-0 sm:max-w-full sm:flex-wrap sm:justify-center sm:overflow-visible sm:px-0"
          >
            {MODULES.map((module) => {
              const Icon = module.icon;
              const selected = module.key === activeKey;
              return (
                <button
                  key={module.key}
                  role="tab"
                  aria-selected={selected}
                  onClick={() => setActiveKey(module.key)}
                  className={cn(
                    "inline-flex shrink-0 items-center gap-2 rounded-full border px-4 py-2 text-sm font-medium transition-colors",
                    selected
                      ? "border-gray-900 bg-gray-900 text-white"
                      : "border-gray-200 bg-white text-gray-600 hover:border-gray-300 hover:text-gray-900",
                  )}
                >
                  <Icon className="h-4 w-4" strokeWidth={1.75} />
                  {module.label}
                </button>
              );
            })}
          </div>
        </div>

        {/* Showcase */}
        <div className="mt-12 grid items-center gap-12 lg:mt-16 lg:grid-cols-[minmax(0,5fr)_minmax(0,8fr)] lg:gap-16">
          <AnimatePresence mode="wait">
            <motion.div
              key={active.key}
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -8 }}
              transition={{ duration: 0.35, ease }}
              className="order-2 lg:order-1"
            >
              <h3 className="text-2xl font-semibold tracking-tight text-gray-900 sm:text-[1.75rem] sm:leading-tight">
                {active.title}
              </h3>
              <p className="mt-4 text-base leading-relaxed text-gray-500">
                {active.description}
              </p>
              <ul className="mt-7 space-y-3.5">
                {active.points.map((point) => (
                  <li
                    key={point}
                    className="flex gap-3 text-[15px] text-gray-700"
                  >
                    <span className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-theme-primary/10 text-theme-primary">
                      <Check className="h-3 w-3" strokeWidth={3} />
                    </span>
                    {point}
                  </li>
                ))}
              </ul>
            </motion.div>
          </AnimatePresence>

          <div className="relative order-1 pb-8 pl-6 sm:pb-10 sm:pl-12 lg:order-2">
            <div className="pointer-events-none absolute -inset-x-10 -top-10 bottom-0 rounded-full bg-theme-primary/10 blur-[90px]" />
            <AnimatePresence mode="wait">
              <motion.div
                key={active.key}
                initial={{ opacity: 0, scale: 0.985 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.4, ease }}
                className="relative"
              >
                <ScreenFrame
                  src={active.screen.src}
                  alt={active.screen.alt}
                  width={1440}
                  height={900}
                />
                {active.companion?.kind === "phone" && (
                  <PhoneFrame
                    src={active.companion.src}
                    alt={active.companion.alt}
                    className="absolute -bottom-8 -left-6 w-[24%] min-w-[92px] max-w-[180px] sm:-bottom-10 sm:-left-12"
                  />
                )}
                {active.companion?.kind === "dialog" && (
                  <div className="absolute -bottom-8 -left-6 w-[30%] overflow-hidden rounded-xl shadow-[0_24px_50px_-24px_rgba(15,23,42,0.6)] ring-1 ring-gray-900/20 sm:-bottom-10 sm:-left-12">
                    <img
                      src={active.companion.src}
                      alt={active.companion.alt}
                      width={680}
                      height={active.companion.height}
                      loading="lazy"
                      className="block h-auto w-full"
                    />
                  </div>
                )}
              </motion.div>
            </AnimatePresence>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Features;
